import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { IconButton } from 'ksnm-common-ui/lib/IconButton';

import { RoutePath } from '@components/AppRouter/constants';
import { NEW_ENTITY_ID } from '@components/AppRouter/hooks/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { useToggleFullSize } from '@src/utils/hooks/useToggleFullSize';
import { confirmDialogStore } from '@stores/confirmDialog';
import { constantStore } from '@stores/constantStore';
import { reportSettingsStore } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';
import type { ReportSettingsDeleteDto } from '@src/api';

import { columns } from '../columns';

interface IActionButton {
  availableVersions: { id: string; name: string | undefined }[];
  update: () => void;
}

const { TABLE_NAMES } = constantStore;

export const ActionButton = observer(({ update, availableVersions }: IActionButton) => {
  const { push } = useHistory();
  const { open: openConfirmDialog } = confirmDialogStore;
  const {
    groupDelete,
    fullSize,
    toggleFullSize,
    checked,
    setChecked,
    toggleTemplateModal,
    toggleCopySettingsModalVisible,
    organizationCode,
  } = reportSettingsStore;

  const {
    CREATE: createReportSettings,
    UPDATE: updateReportSettings,
    DELETE: deleteReportSettings,
  } = usePermission('CmReportSettings');

  const { VIEW: addTemplateView } = usePermission('CmReportSettingsAddTemplate');

  const onToggleFullSize = useToggleFullSize({
    tableName: TABLE_NAMES.DICTIONARY_REPORT_SETTINGS,
    toggle: toggleFullSize,
    columns,
    fullSize,
    update,
  });

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_REPORT_SETTINGS}/${NEW_ENTITY_ID}`);
  }, [push]);

  const onEdit = useCallback(() => {
    push(`${RoutePath.DICTIONARY_REPORT_SETTINGS}/${checked[0]?.report_version_code}`);
  }, [checked, push]);

  const onDelete = useCallback(() => {
    openConfirmDialog({
      text: `Удалить выбранные элементы (${checked.length})?`,
      onOk: () => {
        const items = checked.map((report) => ({
          organizationCode: report.organization_code,
          reportVersionCode: report.report_version_code,
        }));

        groupDelete(items as ReportSettingsDeleteDto[]).then(() => {
          setChecked([]);
          update();
        });
      },
    });
  }, [checked, groupDelete, openConfirmDialog, setChecked, update]);

  return (
    <>
      {organizationCode && createReportSettings && <IconButton onClick={onCreate} iconName="add" title="Добавить" />}
      {updateReportSettings && (
        <IconButton onClick={onEdit} disabled={checked.length !== 1} iconName="edit" title="Редактировать" />
      )}
      {deleteReportSettings && (
        <IconButton onClick={onDelete} disabled={checked.length === 0} iconName="delete" title="Удалить" />
      )}

      {organizationCode && createReportSettings && (
        <IconButton
          onClick={toggleCopySettingsModalVisible}
          disabled={checked.length === 0 || availableVersions.length === 0}
          iconName="copy"
          title="Копировать"
        />
      )}
      <IconButton
        onClick={onToggleFullSize}
        iconName={fullSize ? 'fullScreenOpen' : 'fullScreenClose'}
        title={fullSize ? 'Уменьшить' : 'Увеличить'}
      />
      {addTemplateView && (
        <IconButton onClick={toggleTemplateModal} key="add" title="Добавить шаблон" iconName="addDocument" />
      )}
    </>
  );
});
